import type {
  IStudentAlertTarget,
  PaymentsStatusFilter,
  StudentTabId,
} from 'core/utils/studentAlerts';

/**
 * Pure encode/parse logic for the Student card deep-link state — the slice of
 * view state the card persists in the URL so a reload (or a shared link)
 * restores the same active tab and Payments-tab filters (Area 7, Req 20; the
 * alert navigation in Area 2, Req 6). Port of `core/utils/groupDeepLink.ts`.
 *
 * Decoupled from React/Apollo/react-router so the round-trip and totality
 * guarantees are property-tested in isolation (design Properties 7–9). The
 * `useStudentDeepLinkState` hook is the thin React wrapper.
 *
 * Conventions:
 *   - `overview` is the default tab and is **omitted** from the URL — an absent
 *     `tab` parameter is equivalent to `overview`.
 *   - Every field omits its default/empty value from the encoded params, so the
 *     query string stays minimal and the round-trip holds.
 *   - Parsing is **total**: each field falls back to its default on an
 *     unrecognized value, so `parse` never throws for arbitrary input.
 */

/** URL-encoded view state that survives a reload. */
export interface IStudentDeepLinkState {
  /** Active tab. Default `overview`, represented by an absent `tab` param. */
  tab: StudentTabId;
  /** Payments-tab agreement filter (agreement id or `all`). Default `all` → `?pa`. */
  paymentsAgreement: string;
  /** Payments-tab status filter. Default `all` → `?ps`. */
  paymentsStatus: PaymentsStatusFilter;
}

/** The URL parameter names each field is encoded under. */
const PARAM = {
  tab: 'tab',
  paymentsAgreement: 'pa',
  paymentsStatus: 'ps',
} as const;

/** Per-field safe defaults — the value used when a param is absent/unrecognized. */
const DEFAULT_TAB: StudentTabId = 'overview';
const DEFAULT_PAYMENTS_AGREEMENT = 'all';
const DEFAULT_PAYMENTS_STATUS: PaymentsStatusFilter = 'all';

/** Recognized values for each enumerated field (mirrors the source unions). */
const TAB_IDS: readonly StudentTabId[] = [
  'overview',
  'agreements',
  'payments',
  'invoices',
  'pandadoc',
  'communication',
  'tasks',
];
const PAYMENTS_STATUSES: readonly PaymentsStatusFilter[] = [
  'all',
  'unpaid',
  'paid',
];

const isTabId = (value: string | null): value is StudentTabId =>
  value !== null && (TAB_IDS as readonly string[]).includes(value);

const isPaymentsStatus = (
  value: string | null,
): value is PaymentsStatusFilter =>
  value !== null && (PAYMENTS_STATUSES as readonly string[]).includes(value);

/** Normalize the `search` input to a fresh, non-aliased `URLSearchParams`. */
const toParams = (search: string | URLSearchParams): URLSearchParams =>
  new URLSearchParams(search);

/**
 * Encode a deep-link state into `URLSearchParams`, omitting any field that holds
 * its default value (including the `overview` tab convention). `parse(encode(s))`
 * restores an equal state (Property 8).
 */
export const encode = (state: IStudentDeepLinkState): URLSearchParams => {
  const params = new URLSearchParams();

  if (state.tab !== DEFAULT_TAB) params.set(PARAM.tab, state.tab);
  if (state.paymentsAgreement !== DEFAULT_PAYMENTS_AGREEMENT) {
    params.set(PARAM.paymentsAgreement, state.paymentsAgreement);
  }
  if (state.paymentsStatus !== DEFAULT_PAYMENTS_STATUS) {
    params.set(PARAM.paymentsStatus, state.paymentsStatus);
  }

  return params;
};

/**
 * Parse a query string (or `URLSearchParams`) into a deep-link state. Total and
 * defensive: every field falls back to its own default on an unrecognized value,
 * so this never throws (Property 9). An absent `tab` resolves to `overview`.
 */
export const parse = (
  search: string | URLSearchParams,
): IStudentDeepLinkState => {
  const params = toParams(search);

  const rawTab = params.get(PARAM.tab);
  const tab: StudentTabId = isTabId(rawTab) ? rawTab : DEFAULT_TAB;

  const rawPa = params.get(PARAM.paymentsAgreement);
  const paymentsAgreement = rawPa ?? DEFAULT_PAYMENTS_AGREEMENT;

  const rawPs = params.get(PARAM.paymentsStatus);
  const paymentsStatus: PaymentsStatusFilter = isPaymentsStatus(rawPs)
    ? rawPs
    : DEFAULT_PAYMENTS_STATUS;

  return { tab, paymentsAgreement, paymentsStatus };
};

/**
 * Apply a single alert's navigation target to the current deep-link state.
 *
 * Replaces — never merges — the tab and the Payments status filter from one
 * target: the target's `tab` becomes active, and its `paymentsStatus` (when
 * present) becomes the Payments status filter, otherwise the filter is reset to
 * its default. This guarantees at most one alert's filter is ever active so
 * conflicting cross-target filters cannot accumulate (Property 7). The agreement
 * filter is reset to `all` because an alert targets the whole student, not a
 * single agreement. `prevState` is never mutated.
 */
export const applyAlertNavigation = (
  prevState: IStudentDeepLinkState,
  target: IStudentAlertTarget,
): IStudentDeepLinkState => ({
  ...prevState,
  tab: target.tab,
  paymentsAgreement: DEFAULT_PAYMENTS_AGREEMENT,
  paymentsStatus: target.paymentsStatus ?? DEFAULT_PAYMENTS_STATUS,
});
